import { c as _c } from "react/compiler-runtime";
import { BadgeCheck } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { SellerShopStatus } from "@/api";
import { useSellers } from "@/api/hooks/use-sellers";
import { useShopSalesSummary, useStorefronts } from "@/api/hooks/use-storefronts";
import { AdminDetailModal } from "@/components/admin-detail-modal";
import { DataTablePagination, SortableHead } from "@/components/data-table";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDataTable } from "@/lib/use-data-table";
import { mergeShopRows, type ShopRow } from "@/lib/shop-rows";
import { SellerDetailContent } from "./seller-detail";
import { ShopDetailContent } from "./shop-detail";

// The admin Shops surface unifies two things that are one concept to an
// operator: the public storefront catalog and the seller-onboarding pipeline.
// A row is one seller-shop (keyed by user id), enriched with its catalog
// storefront (location, product count) when it owns one. Catalog storefronts
// with no owning seller are listed too, so nothing is hidden. Moderation
// actions live on the row's detail page — this list just gets you there.
const STATUS_VARIANT: Record<SellerShopStatus, "default" | "secondary" | "outline" | "destructive"> = {
  filling: "outline",
  on_moderation: "default",
  active: "secondary",
  blocked: "destructive"
};
const STATUS_FILTERS: (SellerShopStatus | "all")[] = ["all", "on_moderation", "active", "filling", "blocked"];

// Sort/priority rank for the status column — the actionable "on moderation"
// state floats to the top, so sorting by status surfaces what needs attention.
const STATUS_RANK: Record<SellerShopStatus, number> = {
  on_moderation: 0,
  active: 1,
  filling: 2,
  blocked: 3
};
type LoadState = "idle" | "loading" | "ready" | "error";
type SortKey = "name" | "owner" | "location" | "products" | "status" | "sales" | "created";

// Сегменты по дате создания магазина — «новизна» продавца.
type AgeBucket = "new" | "growing" | "mature";
const AGE_FILTERS: (AgeBucket | "all")[] = ["all", "new", "growing", "mature"];
const ageOf = (createdAt: string | null): AgeBucket | null => {
  if (!createdAt) return null;
  const days = (Date.now() - new Date(createdAt).getTime()) / 86_400_000;
  if (days <= 30) return "new";
  if (days <= 90) return "growing";
  return "mature";
};
export function ShopsPage() {
  const $ = _c(140);
  const {
    t
  } = useTranslation();
  const sellersQuery = useSellers();
  const storefrontsQuery = useStorefronts();
  const salesQuery = useShopSalesSummary();
  const sellers = sellersQuery.data ?? [];
  const storefronts = storefrontsQuery.data ?? [];
  const salesById = new Map((salesQuery.data ?? []).map(_temp));
  const loadError = sellersQuery.error ?? storefrontsQuery.error;
  const load = loadError ? "error" : sellersQuery.isPending || storefrontsQuery.isPending ? "loading" : "ready";
  const error = loadError ? loadError.message : null;
  const [status, setStatus] = useState("all");
  const [age, setAge] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const rows = mergeShopRows(sellers, storefronts, salesById);
  const query = search.trim().toLowerCase();
  const filtered = rows.filter(r => {
    if (status !== "all" && r.status !== status) {
      return false;
    }
    if (age !== "all" && ageOf(r.createdAt) !== age) {
      return false;
    }
    if (!query) {
      return true;
    }
    return r.name.toLowerCase().includes(query) || r.sellerName.toLowerCase().includes(query);
  });
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      key: "status",
      dir: "asc"
    };
    t1 = {
      name: _temp2,
      owner: _temp3,
      location: _temp4,
      products: _temp5,
      status: _temp6,
      sales: _temp7,
      created: _temp8
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== filtered) {
    t2 = {
      rows: filtered,
      pageSize: 25,
      initialSort: t0,
      accessors: t1
    };
    $[2] = filtered;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const table = useDataTable(t2);
  let t3;
  if ($[4] !== t) {
    t3 = t("storefronts.heading");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <h1 className="text-2xl font-semibold">{t3}</h1>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("storefronts.intro");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <p className="text-sm text-muted-foreground">{t5}</p>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <header className="space-y-1">{t4}{t6}</header>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== setStatus || $[16] !== status || $[17] !== t || $[18] !== table) {
    t8 = STATUS_FILTERS.map(s_0 => <Button key={s_0} variant={s_0 === status ? "default" : "outline"} size="sm" onClick={() => {
      table.setPage(1);
      setStatus(s_0);
    }}>{s_0 === "all" ? t("storefronts.filterAll") : t(`sellers.status.${s_0}`)}</Button>);
    $[15] = setStatus;
    $[16] = status;
    $[17] = t;
    $[18] = table;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <div className="flex flex-wrap items-center gap-2">{t8}</div>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t) {
    t10 = t("storefronts.ageFilter", {
      defaultValue: "\u0421\u043E\u0437\u0434\u0430\u043D:"
    });
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10) {
    t11 = <span className="text-xs text-muted-foreground">{t10}</span>;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== age || $[27] !== setAge || $[28] !== t || $[29] !== table) {
    t12 = AGE_FILTERS.map(a => <Button key={a} variant={a === age ? "default" : "outline"} size="sm" onClick={() => {
      table.setPage(1);
      setAge(a);
    }}>{a === "all" ? t("storefronts.filterAll") : t(`storefronts.age.${a}`, {
        defaultValue: a === "new" ? "\u0434\u043E 30 \u0434\u043D\u0435\u0439" : a === "growing" ? "30\u201390 \u0434\u043D\u0435\u0439" : "\u0431\u043E\u043B\u0435\u0435 90 \u0434\u043D\u0435\u0439"
      })}</Button>);
    $[26] = age;
    $[27] = setAge;
    $[28] = t;
    $[29] = table;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t11 || $[32] !== t12) {
    t13 = <div className="flex flex-wrap items-center gap-2">{t11}{t12}</div>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== setSearch || $[35] !== table) {
    t14 = e => {
      table.setPage(1);
      setSearch(e.target.value);
    };
    $[34] = setSearch;
    $[35] = table;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t) {
    t15 = t("storefronts.searchPlaceholder");
    $[37] = t;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== search || $[40] !== t14 || $[41] !== t15) {
    t16 = <Input value={search} onChange={t14} placeholder={t15} className="h-9 w-full sm:max-w-xs" />;
    $[39] = search;
    $[40] = t14;
    $[41] = t15;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== error) {
    t17 = error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>;
    $[43] = error;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== t) {
    t18 = t("storefronts.col.shop");
    $[45] = t;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t18 || $[48] !== table.sort || $[49] !== table.toggleSort) {
    t19 = <SortableHead label={t18} sortKey="name" sort={table.sort} onToggle={table.toggleSort} />;
    $[47] = t18;
    $[48] = table.sort;
    $[49] = table.toggleSort;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== t) {
    t20 = t("storefronts.col.owner");
    $[51] = t;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== t20 || $[54] !== table.sort || $[55] !== table.toggleSort) {
    t21 = <SortableHead label={t20} sortKey="owner" sort={table.sort} onToggle={table.toggleSort} />;
    $[53] = t20;
    $[54] = table.sort;
    $[55] = table.toggleSort;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== t) {
    t22 = t("storefronts.col.location");
    $[57] = t;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== t22 || $[60] !== table.sort || $[61] !== table.toggleSort) {
    t23 = <SortableHead label={t22} sortKey="location" sort={table.sort} onToggle={table.toggleSort} />;
    $[59] = t22;
    $[60] = table.sort;
    $[61] = table.toggleSort;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== t) {
    t24 = t("storefronts.col.products");
    $[63] = t;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== t24 || $[66] !== table.sort || $[67] !== table.toggleSort) {
    t25 = <SortableHead label={t24} sortKey="products" sort={table.sort} onToggle={table.toggleSort} align="right" className="text-right" />;
    $[65] = t24;
    $[66] = table.sort;
    $[67] = table.toggleSort;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  let t26;
  if ($[69] !== t) {
    t26 = t("storefronts.col.sales", {
      defaultValue: "\u041F\u0440\u043E\u0434\u0430\u0436\u0438"
    });
    $[69] = t;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== t26 || $[72] !== table.sort || $[73] !== table.toggleSort) {
    t27 = <SortableHead label={t26} sortKey="sales" sort={table.sort} onToggle={table.toggleSort} align="right" className="text-right" />;
    $[71] = t26;
    $[72] = table.sort;
    $[73] = table.toggleSort;
    $[74] = t27;
  } else {
    t27 = $[74];
  }
  let t28;
  if ($[75] !== t) {
    t28 = t("storefronts.col.status");
    $[75] = t;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== t28 || $[78] !== table.sort || $[79] !== table.toggleSort) {
    t29 = <SortableHead label={t28} sortKey="status" sort={table.sort} onToggle={table.toggleSort} />;
    $[77] = t28;
    $[78] = table.sort;
    $[79] = table.toggleSort;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  let t30;
  if ($[81] !== t19 || $[82] !== t21 || $[83] !== t23 || $[84] !== t25 || $[85] !== t27 || $[86] !== t29) {
    t30 = <TableHeader><TableRow>{t19}{t21}{t23}{t25}{t27}{t29}</TableRow></TableHeader>;
    $[81] = t19;
    $[82] = t21;
    $[83] = t23;
    $[84] = t25;
    $[85] = t27;
    $[86] = t29;
    $[87] = t30;
  } else {
    t30 = $[87];
  }
  let t31;
  if ($[88] !== load || $[89] !== t) {
    t31 = load === "loading" && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">{t("storefronts.loading")}</TableCell></TableRow>;
    $[88] = load;
    $[89] = t;
    $[90] = t31;
  } else {
    t31 = $[90];
  }
  let t32;
  if ($[91] !== load || $[92] !== t || $[93] !== table.total) {
    t32 = load === "ready" && table.total === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">{t("storefronts.empty")}</TableCell></TableRow>;
    $[91] = load;
    $[92] = t;
    $[93] = table.total;
    $[94] = t32;
  } else {
    t32 = $[94];
  }
  let t33;
  if ($[95] !== setSelected || $[96] !== t || $[97] !== table.pageRows) {
    let t34;
    if ($[99] !== setSelected || $[100] !== t) {
      t34 = r_7 => <TableRow key={r_7.key} className="cursor-pointer" onClick={() => setSelected(r_7)}><TableCell className="font-medium"><span className="inline-flex items-center gap-1.5">{r_7.name}{r_7.verified && <BadgeCheck className="size-4 text-emerald-600" aria-label={t("sellers.verifiedBadge")} />}</span></TableCell><TableCell className="text-muted-foreground">{r_7.sellerName || "\u2014"}</TableCell><TableCell className="text-muted-foreground">{r_7.location || "\u2014"}</TableCell><TableCell className="text-right tabular-nums">{r_7.productCount ?? "\u2014"}</TableCell><TableCell className="text-right tabular-nums">{r_7.sales > 0 ? <span>{Math.round(r_7.sales).toLocaleString("ru-RU")} ₽<span className="ml-1 text-xs text-muted-foreground">· {r_7.orders}</span></span> : "\u2014"}</TableCell><TableCell><Badge variant={STATUS_VARIANT[r_7.status]}>{t(`sellers.status.${r_7.status}`)}</Badge></TableCell></TableRow>;
      $[99] = setSelected;
      $[100] = t;
      $[101] = t34;
    } else {
      t34 = $[101];
    }
    t33 = table.pageRows.map(t34);
    $[95] = setSelected;
    $[96] = t;
    $[97] = table.pageRows;
    $[98] = t33;
  } else {
    t33 = $[98];
  }
  let t34;
  if ($[102] !== t31 || $[103] !== t32 || $[104] !== t33) {
    t34 = <TableBody>{t31}{t32}{t33}</TableBody>;
    $[102] = t31;
    $[103] = t32;
    $[104] = t33;
    $[105] = t34;
  } else {
    t34 = $[105];
  }
  let t35;
  if ($[106] !== t30 || $[107] !== t34) {
    t35 = <Table>{t30}{t34}</Table>;
    $[106] = t30;
    $[107] = t34;
    $[108] = t35;
  } else {
    t35 = $[108];
  }
  let t36;
  if ($[109] !== load || $[110] !== table) {
    t36 = load === "ready" && table.total > 0 && <DataTablePagination table={table} />;
    $[109] = load;
    $[110] = table;
    $[111] = t36;
  } else {
    t36 = $[111];
  }
  const t37 = !!selected;
  let t38;
  if ($[112] !== selected?.name || $[113] !== t) {
    t38 = selected?.name ?? t("storefronts.heading");
    $[112] = selected?.name;
    $[113] = t;
    $[114] = t38;
  } else {
    t38 = $[114];
  }
  const t39 = selected?.href;
  let t40;
  if ($[115] !== t) {
    t40 = t("detailModal.openFullPage");
    $[115] = t;
    $[116] = t40;
  } else {
    t40 = $[116];
  }
  let t41;
  if ($[117] !== setSelected) {
    t41 = open => !open && setSelected(null);
    $[117] = setSelected;
    $[118] = t41;
  } else {
    t41 = $[118];
  }
  let t42;
  if ($[119] !== selected) {
    t42 = selected?.detail.kind === "storefront" && <ShopDetailContent slug={selected.detail.slug} />;
    $[119] = selected;
    $[120] = t42;
  } else {
    t42 = $[120];
  }
  let t43;
  if ($[121] !== selected) {
    t43 = selected?.detail.kind === "seller" && <SellerDetailContent userId={selected.detail.userId} />;
    $[121] = selected;
    $[122] = t43;
  } else {
    t43 = $[122];
  }
  let t44;
  if ($[123] !== t37 || $[124] !== t38 || $[125] !== t39 || $[126] !== t40 || $[127] !== t41 || $[128] !== t42 || $[129] !== t43) {
    t44 = <AdminDetailModal open={t37} title={t38} fullPageHref={t39} fullPageLabel={t40} onOpenChange={t41}>{t42}{t43}</AdminDetailModal>;
    $[123] = t37;
    $[124] = t38;
    $[125] = t39;
    $[126] = t40;
    $[127] = t41;
    $[128] = t42;
    $[129] = t43;
    $[130] = t44;
  } else {
    t44 = $[130];
  }
  let t45;
  if ($[131] !== t13 || $[132] !== t16 || $[133] !== t17 || $[134] !== t35 || $[135] !== t36 || $[136] !== t44 || $[137] !== t7 || $[138] !== t9) {
    t45 = <div className="space-y-6">{t7}{t9}{t13}{t16}{t17}{t35}{t36}{t44}</div>;
    $[131] = t13;
    $[132] = t16;
    $[133] = t17;
    $[134] = t35;
    $[135] = t36;
    $[136] = t44;
    $[137] = t7;
    $[138] = t9;
    $[139] = t45;
  } else {
    t45 = $[139];
  }
  return t45;
}
function _temp8(r_6) {
  return r_6.createdAt ? new Date(r_6.createdAt).getTime() : 0;
}
function _temp7(r_5) {
  return r_5.sales;
}
function _temp6(r_4) {
  return STATUS_RANK[r_4.status];
}
function _temp5(r_3) {
  return r_3.productCount;
}
function _temp4(r_2) {
  return r_2.location;
}
function _temp3(r_1) {
  return r_1.sellerName;
}
function _temp2(r_0) {
  return r_0.name;
}
function _temp(s) {
  return [s.shopId, s];
}